"use client";

import React, { useEffect } from "react";
import { Phone, ShieldAlert, Clock, MapPin } from 'lucide-react';
import { Button } from "@/components/ui/button";
export default function RequestService() {
  useEffect(() => {
    const cssHref = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/external/work_request_embed.css";
    if (!document.querySelector(`link[href="${cssHref}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.media = "screen";
      link.href = cssHref;
      document.head.appendChild(link);
    }
    const scriptSrc = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/static_link/work_request_embed_snippet.js";
    const script = document.createElement("script");
    script.src = scriptSrc;
    script.setAttribute("clienthub_id", "476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587");
    script.setAttribute("form_url", "https://clienthub.getjobber.com/client_hubs/476c5fe2-f16a-4108-ad33-1763e02c3dad/public/work_request/embedded_work_request_form?form_id=4972587");
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  return <section id="request-service" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-primary/10 border border-primary/20 text-primary font-mono text-xs uppercase tracking-wider font-semibold mb-4">
            <span className="inline-block size-2 rounded-full bg-primary animate-pulse" />
            24/7 Rapid Dispatch Station • MD / DC / NOVA
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-default tracking-tight text-foreground leading-tight">
            Request emergency restoration
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Tell us where you are and what happened. Local crews deploy directly across Maryland, Washington DC, and Northern Virginia without call-center delays.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Main Form Column (7 cols) */}
          <div className="lg:col-span-7">
            <div className="bg-card border border-border rounded-xl p-6 sm:p-8 shadow-sm">
              <div id="476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587" />
            </div>
          </div>

          {/* Right Column: High-Contrast Emergency Call & Dispatch Box (5 cols) */}
          <div className="lg:col-span-5 space-y-6">
            {/* Primary High-Contrast Structural Purple Dispatch Box */}
            <div className="bg-secondary text-secondary-foreground rounded-xl p-7 sm:p-8 border border-secondary/40 shadow-md relative overflow-hidden">
              <div className="flex items-center gap-2 text-accent font-mono text-xs uppercase tracking-wider font-bold mb-3">
                <ShieldAlert className="size-4 shrink-0" />
                Active Incident Response
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold font-default text-secondary-foreground leading-tight mb-3">
                Need crews right now?
              </h3>

              <p className="text-secondary-foreground/80 text-sm leading-relaxed mb-6">
                Active fire and structural breaches require prompt containment. Call our direct regional field office for rapid board-up, roof tarping, and emergency structural stabilization.
              </p>

              {/* Direct Click-to-call link for 855-435-6631 */}
              <div className="p-4 rounded-lg bg-background/10 border border-white/10 mb-6 backdrop-blur-xs">
                <span className="text-xs uppercase tracking-wider font-mono text-secondary-foreground/70 block mb-1">
                  Direct Dispatch Hotline
                </span>
                <a href="tel:855-435-6631" className="flex items-center justify-between text-2xl sm:text-3xl font-mono font-bold text-accent hover:underline decoration-2 underline-offset-4 focus-visible:ring-2 focus-visible:ring-accent rounded-sm outline-none" aria-label="Call TriServe emergency dispatch at 855-435-6631">
                  <span>855-435-6631</span>
                  <Phone className="size-6 shrink-0 text-accent animate-pulse" />
                </a>
              </div>

              <div className="space-y-3 pt-2 border-t border-white/10 text-xs text-secondary-foreground/90 font-medium">
                <div className="flex items-start gap-2.5">
                  <Clock className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>Crews on standby 24 hours a day, 365 days a year</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <ShieldAlert className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>Immediate board-up and temporary weather seals</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <MapPin className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>Direct technician response across MD, DC, and Northern VA</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <Button asChild className="w-full bg-accent text-accent-foreground font-bold hover:bg-accent/90 shadow-sm">
                  <a href="tel:855-435-6631">
                    <Phone className="size-4 mr-2" />
                    Call 855-435-6631 Now
                  </a>
                </Button>
              </div>
            </div>

            {/* Direct Note Regarding Rapid Board-Up & Containment Dispatch */}
            <div className="bg-muted/80 border border-border rounded-xl p-6 text-foreground">
              <h4 className="text-base font-bold font-default text-foreground flex items-center gap-2 mb-2">
                <ShieldAlert className="size-4 text-primary" />
                Emergency Board-Up & Containment Note
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Fire departments often release properties with exposed windows, broken doors, and compromised roofs. TriServe dispatches dedicated board-up vehicles carrying heavy timber framing, commercial tarps, and structural bracing to secure your property against unauthorized entry and secondary weather damage within hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}